import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { hasModule, firstAllowedPath } from "@/lib/permissions";
import Layout from "@/components/layout/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeProfile from "@/pages/EmployeeProfile";
import OrgChart from "@/pages/OrgChart";
import Attendance from "@/pages/Attendance";
import Leaves from "@/pages/Leaves";
import Performance from "@/pages/Performance";
import Payroll from "@/pages/Payroll";
import Increments from "@/pages/Increments";
import Loans from "@/pages/Loans";
import Designations from "@/pages/Designations";
import Assets from "@/pages/Assets";
import Tada from "@/pages/Tada";
import Recruitment from "@/pages/Recruitment";
import Exits from "@/pages/Exits";
import Reports from "@/pages/Reports";
import AISummaries from "@/pages/AISummaries";
import MyProfile from "@/pages/MyProfile";
import PunchIn from "@/pages/PunchIn";
import Locations from "@/pages/Locations";
import Notifications from "@/pages/Notifications";
import AdminDangerZone from "@/pages/AdminDangerZone";
import Users from "@/pages/Users";
import Workers from "@/pages/Workers";
import FieldStaff from "@/pages/FieldStaff";
import Vehicles from "@/pages/Vehicles";
import Compliance from "@/pages/Compliance";
import MobileNumbers from "@/pages/MobileNumbers";
import { Loader2, ShieldOff } from "lucide-react";

function NoAccess() {
  return (
    <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="no-access-page">
      <ShieldOff className="w-12 h-12 text-stone-300 mb-4" />
      <h2 className="font-heading text-xl font-bold">No module access</h2>
      <p className="text-sm text-stone-500 mt-2 max-w-sm">
        Your account doesn't have access to any modules yet. Please ask your administrator to grant access in Users &amp; Access.
      </p>
    </div>
  );
}

function Protected({ children, module, adminOnly }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-page)" }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--color-primary)" }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const denied = (adminOnly && user.role !== "super_admin") || (module && !hasModule(user, module));
  if (denied) {
    const fallback = firstAllowedPath(user);
    if (fallback && fallback !== location.pathname) return <Navigate to={fallback} replace />;
    return <Layout><NoAccess /></Layout>;
  }
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected module="dashboard"><Dashboard /></Protected>} />
            <Route path="/employees" element={<Protected module="employees"><Employees /></Protected>} />
            <Route path="/employees/:id" element={<Protected module="employees"><EmployeeProfile /></Protected>} />
            <Route path="/org-chart" element={<Protected module="org_chart"><OrgChart /></Protected>} />
            <Route path="/attendance" element={<Protected module="attendance"><Attendance /></Protected>} />
            <Route path="/leaves" element={<Protected module="leaves"><Leaves /></Protected>} />
            <Route path="/performance" element={<Protected module="performance"><Performance /></Protected>} />
            <Route path="/payroll" element={<Protected module="payroll"><Payroll /></Protected>} />
            <Route path="/increments" element={<Protected module="increments"><Increments /></Protected>} />
            <Route path="/loans" element={<Protected module="loans"><Loans /></Protected>} />
            <Route path="/designations" element={<Protected module="designations"><Designations /></Protected>} />
            <Route path="/assets" element={<Protected module="assets"><Assets /></Protected>} />
            <Route path="/tada" element={<Protected module="tada"><Tada /></Protected>} />
            <Route path="/recruitment" element={<Protected module="recruitment"><Recruitment /></Protected>} />
            <Route path="/exits" element={<Protected module="exits"><Exits /></Protected>} />
            <Route path="/reports" element={<Protected module="reports"><Reports /></Protected>} />
            <Route path="/ai-summaries" element={<Protected module="reports"><AISummaries /></Protected>} />
            <Route path="/me" element={<Protected module="my_profile"><MyProfile /></Protected>} />
            <Route path="/punch" element={<Protected module="punch"><PunchIn /></Protected>} />
            <Route path="/locations" element={<Protected module="locations"><Locations /></Protected>} />
            <Route path="/notifications" element={<Protected module="notifications"><Notifications /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><AdminDangerZone /></Protected>} />
            <Route path="/users" element={<Protected adminOnly><Users /></Protected>} />
            <Route path="/workers" element={<Protected module="workers"><Workers /></Protected>} />
            <Route path="/field-staff" element={<Protected module="field_staff"><FieldStaff /></Protected>} />
            <Route path="/vehicles" element={<Protected module="vehicles"><Vehicles /></Protected>} />
            <Route path="/compliance" element={<Protected module="compliance"><Compliance /></Protected>} />
            <Route path="/mobile-numbers" element={<Protected module="mobile_numbers"><MobileNumbers /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
